import { baseApi } from "common/redux/core/api.base";
import { ProcessingHubService } from "../../services/processing-hub.service";
import {
  UploadRequest,
  MultiUploadRequest,
  BatchUploadRequest,
  UploadResponse,
  DocumentActionRequest,
  BatchCreateRequest,
  BatchCreateResponse
} from "../../models/processing-hub.model";
import { ApiCacheTags } from "common/models/api-base.model";
import { ApiResult } from "common/models";
import { withApiResponse } from "common/utils/rtk-query.util";

const processingHubCommandsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    uploadDocuments: build.mutation<UploadResponse, UploadRequest>({
      queryFn: (uploadRequest: UploadRequest) => {
        return ProcessingHubService.uploadDocuments(uploadRequest);
      },
      invalidatesTags: [ApiCacheTags.DOCUMENT_LIST]
    }),

    multiUploadDocuments: build.mutation<UploadResponse, MultiUploadRequest>({
      queryFn: (uploadRequest: MultiUploadRequest) => {
        return ProcessingHubService.multiUploadDocuments(uploadRequest);
      },
      invalidatesTags: [ApiCacheTags.DOCUMENT_LIST]
    }),

    deleteDocument: build.mutation<any, string | number>({
      queryFn: (documentId: string | number) => {
        return withApiResponse<void>(async () => {
          const response = await ProcessingHubService.deleteDocument(
            documentId
          );
          return new ApiResult<void>(
            response.value,
            response.statusCode,
            ...response.errors
          );
        });
      },
      invalidatesTags: [ApiCacheTags.DOCUMENT_LIST]
    }),

    processDocument: build.mutation<any, DocumentActionRequest>({
      queryFn: (actionRequest: DocumentActionRequest) => {
        return ProcessingHubService.processDocument(actionRequest);
      },
      invalidatesTags: [ApiCacheTags.DOCUMENT_LIST]
    }),

    batchUpload: build.mutation<
      UploadResponse,
      { uploadRequest: BatchUploadRequest; userEmail?: string }
    >({
      queryFn: ({ uploadRequest, userEmail }) => {
        return ProcessingHubService.batchUpload(uploadRequest, userEmail);
      },
      invalidatesTags: [ApiCacheTags.DOCUMENT_LIST]
    }),

    refreshDocuments: build.mutation<any, void>({
      queryFn: () => {
        return ProcessingHubService.refreshDocuments();
      },
      invalidatesTags: [ApiCacheTags.DOCUMENT_LIST]
    }),

    deleteGDXDocument: build.mutation<
      any,
      { documentId: string | number; flowId: string | number }
    >({
      queryFn: ({ documentId, flowId }) => {
        return withApiResponse<void>(async () => {
          const response = await ProcessingHubService.deleteGDXDocument(
            documentId,
            flowId
          );
          return new ApiResult<void>(
            response.value,
            response.statusCode,
            ...response.errors
          );
        });
      },
      invalidatesTags: [ApiCacheTags.DOCUMENT_LIST]
    }),

    createBatch: build.mutation<BatchCreateResponse, BatchCreateRequest>({
      queryFn: (batchRequest: BatchCreateRequest) => {
        return ProcessingHubService.createBatch(batchRequest);
      }
    }),
    exportExtractedDataExcel: build.mutation<Blob, string | number>({
      queryFn: async (documentId: string | number) => {
        try {
          const result = await ProcessingHubService.exportExtractedDataExcel(
            documentId
          );
          if (result.errors && result.errors.length > 0) {
            return {
              error: { status: result.statusCode, data: result.errors }
            };
          }
          return { data: result.value };
        } catch (error: any) {
          return {
            error: { status: 500, data: error.message || "Export failed" }
          };
        }
      }
    }),

    updateGdxDocument: build.mutation<
      any,
      { flowId: string | number; docId: string | number; updateData: any }
    >({
      queryFn: ({ flowId, docId, updateData }) => {
        return ProcessingHubService.updateGdxDocument(
          flowId,
          docId,
          updateData
        );
      },
      invalidatesTags: [ApiCacheTags.DOCUMENT_LIST]
    })
  })
});

export const {
  useUploadDocumentsMutation,
  useMultiUploadDocumentsMutation,
  useBatchUploadMutation,
  useDeleteDocumentMutation,
  useProcessDocumentMutation,
  useRefreshDocumentsMutation,
  useDeleteGDXDocumentMutation,
  useCreateBatchMutation,
  useUpdateGdxDocumentMutation,
  useExportExtractedDataExcelMutation
} = processingHubCommandsApi;
